import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface LoadingScreenProps {
  onLoadComplete?: () => void;
  minLoadTime?: number;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
  onLoadComplete, 
  minLoadTime = 1000 
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const startTime = Date.now();
    
    // Function to ensure we display loading screen for at least minLoadTime
    const completeLoading = () => {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadTime - elapsedTime);
      
      setTimeout(() => {
        setIsVisible(false);
        if (onLoadComplete) onLoadComplete();
      }, remainingTime);
    };

    // Start timer when all resources are loaded
    if (document.readyState === 'complete') {
      completeLoading();
    } else {
      window.addEventListener('load', completeLoading);
      return () => window.removeEventListener('load', completeLoading);
    }
  }, [minLoadTime, onLoadComplete]);

  if (!isVisible) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ 
          type: "spring",
          stiffness: 260,
          damping: 20,
          duration: 0.6 
        }}
        className="flex flex-col items-center"
      >
        <motion.img
          src="/fictional.stream-logo.svg"
          alt="fictional.stream Logo"
          className="h-20 w-20 text-primary"
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ 
            scale: { 
              repeat: Infinity, 
              duration: 2, 
              ease: "easeInOut",
              times: [0, 0.5, 1]
            },
            rotate: {
              repeat: Infinity,
              duration: 3,
              ease: "easeInOut",
              times: [0, 0.25, 0.75, 1]
            }
          }}
        />
      </motion.div>
    </motion.div>
  );
};

export default LoadingScreen;
